import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  Calendar,
  DollarSign,
  Clock,
  Globe,
  FileText,
  Target,
  Award,
  Users,
  BookOpen,
  CheckCircle,
  Download,
  Mail,
  ArrowRight,
  Scale,
  Building,
  Music,
  Briefcase,
  Monitor,
  Shield,
  TrendingUp,
  Lightbulb,
  Brain,
  Star,
  Loader2,
  BrainCircuit
} from 'lucide-react';

const Postdoctorados = () => {
  // Estados para datos dinámicos
  const [programaData, setProgramaData] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos desde JSON
  useEffect(() => {
    const loadProgramaData = async () => {
      try {
        setIsLoading(true);
        
        // Cargar datos del programa y datos generales en paralelo
        const [programaResponse, generalesResponse] = await Promise.all([
          fetch(`/data/postdoctorados.json?v=${Date.now()}`),
          fetch(`/data/generales.json?v=${Date.now()}`)
        ]);
        
        if (!programaResponse.ok) {
          throw new Error('Error al cargar los datos del programa');
        }
        if (!generalesResponse.ok) {
          throw new Error('Error al cargar datos generales');
        }
        
        const programaData = await programaResponse.json();
        const generalesData = await generalesResponse.json();
        
        setProgramaData(programaData);
        setDatosBancarios(generalesData.datosBancarios);
        setError(null);
      } catch (err) {
        console.error('Error cargando datos de postdoctorados:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgramaData();
  }, []);

  // Datos dinámicos desde JSON
  const calendarios = programaData?.calendarios || {};

  const costos = programaData?.costos || {
    total: 'Cargando...',
    mensualidad: 'Cargando...',
    duracion: 'Cargando...'
  };

  const modalidad = programaData?.modalidad || {
    tipo: 'Cargando...',
    metodologia: 'Cargando...',
    plataforma: 'Cargando...',
    contacto: 'postdoctoradoscenid@cenid.org'
  };

  const estadisticas = programaData?.estadisticas || {
    graduados: 0,
    generaciones: 0,
    articulos: 0
  };

  const objetivosPostdoctorado = [
    'Participación en seminarios y talleres de especialización.',
    'Orientar la investigación hacia el entendimiento y la solución de la problemática académica y profesional.',
    'Formar profesionales en investigación con comprensión y autonomía hacía las diferentes áreas con capacidad analítica, crítica e innovadora.',
    'Gestión de proyectos de investigación con calidad y publicaciones individuales y colectivas, nacionales e internacionales que faciliten el intercambio inter y transdiciplinario en las diferentes áreas del conocimiento.',
    'Vitalizar el desenvolvimiento de una actitud reflexiva analítica y crítica, orientada por visiones de carácter interdisciplinar y transdisciplinar en el análisis de las problemáticas educativas.'
  ];

  const primeraEtapa = [
    'Gestión del conocimiento',
    'Diseño y elaboración de proyectos de investigación humanística, científica y de innovación',
    'Cultura científica y apropiación de la ciencia',
    'Inteligencia Artificial en la investigación científica'
  ];

  const segundaEtapa = [
    'Taller: Redacción científica',
    'Taller: Elaboración de artículos científicos',
    'Taller: Transferencia y uso de la información científica'
  ];

  // Mapeo de iconos desde strings a componentes de lucide-react
  const iconMap = {
    BrainCircuit,
    Building,
    Award,
    Monitor,
    Globe,
    Shield,
    Briefcase,
    BookOpen,
    Scale,
    Target,
    TrendingUp,
    Users,
    Lightbulb,
    Brain,
    Star,
    Music
  };

  // Cargar líneas de investigación desde JSON y mapear iconos
  const lineasInvestigacion = (programaData?.lineasInvestigacion || []).map(linea => ({
    ...linea,
    icono: iconMap[linea.icono] || BookOpen // Fallback a BookOpen si el icono no existe
  }));

  const documentosRequeridos = [
    'Título de doctorado o carta de presentación de examen',
    'Carta de exposición de motivos (Exponer en forma precisa las razones personales y profesionales)',
    'Resumen del proyecto de investigación con que se presentara para el programa',
    'Currículum Vitae. (Comprobable de los últimos 5 años)',
    'CURP',
    'Comprobante de Domicilio',
    'Acta de nacimiento',
    'Recibo de la transferencia bancaria del pago (primera estancia)'
  ];

  const costosIncluidos = [
    'Emisión de constancias a cada seminario.',
    'Emisión de constancias a cada taller.',
    'Certificado total del postdoctorado.',
    'Certificado de participación en la estancia académica.',
    '*Publicación artículo científico arbitrado en formato electrónico.'
  ];

  // Cargar comité desde JSON
  const comitePostdoctorado = programaData?.comitePostdoctorado || [];

  // Mostrar estado de carga
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información del programa...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si falla la carga
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 font-semibold text-xl mb-2">Error al cargar la información</p>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://recursos.cenid.org/img_cenid/servicios/img_postdoctorado2.jpg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <GraduationCap className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Postdoctorado en educación
              </h1>
              <p className="text-xl text-green-100 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Formación postdoctoral de excelencia
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-4 gap-6 -mt-12 mb-16 relative z-10">
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900">Duración</h3>
            </div>
            <p className="text-2xl font-bold text-[#0b742f]">{costos.duracion}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#e87729] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="font-bold text-gray-900">Modalidad</h3>
            </div>
            <p className="text-2xl font-bold text-[#e87729]">{modalidad.tipo}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900">Costo Total</h3>
            </div>
            <p className="text-lg font-bold text-[#0b742f]">{costos.total}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-purple-500 hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-purple-500/10 p-3 rounded-lg">
                <Users className="w-6 h-6 text-purple-500" />
              </div>
              <h3 className="font-bold text-gray-900">Graduados</h3>
            </div>
            <p className="text-2xl font-bold text-purple-500">{estadisticas.graduados}</p>
          </div>
        </div>

        {/* Modalidad e Información */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Modalidad */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-8 h-8 text-[#0b742f]" />
                <h3 className="text-2xl font-bold text-gray-900">Metodología</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">Modalidad:</span>
                  <span className="font-semibold text-[#0b742f]">{modalidad.tipo}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">Sesiones:</span>
                  <span className="font-semibold text-gray-900">{modalidad.metodologia}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">Plataforma:</span>
                  <span className="font-semibold text-gray-900">{modalidad.plataforma}</span>
                </div>
                <div className="space-y-2">
                  <span className="text-gray-600 block">Contacto:</span>
                  <a href={`mailto:${modalidad.contacto}`} className="font-semibold text-[#0b742f] hover:underline text-sm">{modalidad.contacto}</a>
                </div>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-8 h-8" />
                <h3 className="text-2xl font-bold">Estadísticas del programa</h3>
              </div>
              <div className="space-y-6">
                <div>
                  <p className="text-orange-100 text-sm mb-1">Graduados</p>
                  <p className="text-4xl font-bold">{estadisticas.graduados}</p>
                </div>
                <div>
                  <p className="text-orange-100 text-sm mb-1">Generaciones</p>
                  <p className="text-4xl font-bold">{estadisticas.generaciones}</p>
                </div>
                <div>
                  <p className="text-orange-100 text-sm mb-1">Artículos publicados</p>
                  <p className="text-4xl font-bold">{estadisticas.articulos}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Inscripciones */}
        <section className="mb-16">
          {/* Agrupar calendarios por año */}
          {(() => {
            // Agrupar calendarios por año
            const calendariosPorAnio = {};
            Object.entries(calendarios).forEach(([key, calendario]) => {
              const anio = calendario.nombre.split('-')[0];
              if (!calendariosPorAnio[anio]) {
                calendariosPorAnio[anio] = [];
              }
              calendariosPorAnio[anio].push({ key, ...calendario });
            });

            // Ordenar años de forma descendente (más reciente primero)
            const aniosOrdenados = Object.keys(calendariosPorAnio).sort((a, b) => b.localeCompare(a));

            return aniosOrdenados.map((anio) => (
              <div key={anio} className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f] mb-8">
                <div className="flex items-center gap-3 mb-8">
                  <Calendar className="w-8 h-8 text-[#0b742f]" />
                  <h2 className="text-3xl font-bold text-gray-900">
                    Calendarios {anio}
                  </h2>
                </div>

                {/* Calendarios del año */}
                {calendariosPorAnio[anio].map((calendario, index) => (
                  <div key={calendario.key} className={index < calendariosPorAnio[anio].length - 1 ? "mb-8" : ""}>
                    <h4 className="text-xl font-bold text-gray-900 mb-4">Calendario {calendario.nombre}</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-green-50 rounded-lg p-6">
                        <p className="text-sm text-gray-600 mb-2">Periodo de inscripción</p>
                        <p className="text-lg font-bold text-[#0b742f]">{calendario.periodo}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-6">
                        <p className="text-sm text-gray-600 mb-2">Inicio de actividades</p>
                        <p className="text-lg font-bold text-[#e87729]">{calendario.inicio}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </section>

        {/* Objetivo del Programa */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Imágenes Proyecto Cátedra */}
            <div className="flex justify-center items-center gap-8 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <img
                  src="https://recursos.cenid.org/img_cenid/proyecto_catedra.png"
                  alt="Proyecto Cátedra"
                  className="h-24 w-auto object-contain"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <Target className="w-10 h-10 text-[#e87729]" />
              <h2 className="text-3xl font-bold">Objetivo del Programa</h2>
            </div>
            <p className="text-lg leading-relaxed text-white/95 mb-6">
              El programa postdoctoral está integrado por un grupo de doctores con reconocimiento en la docencia e investigación en el área de estudio específica, seleccionados, dirigidos y supervisados por el CENID, mediante el estudio, análisis y la realización de informes de investigación, en una simbiosis entre práctica y teoría que implica una participación en su propia formación, mediante la intervención directa del alumno aplicando las herramientas de la metodología de investigación científica.
            </p>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="text-sm">✨ Se vinculará con la construcción del <strong>Proyecto Cátedra sobre análisis e inclusión del conocimiento del CENID</strong> con participación de <strong>CÁTEDRAS UNESCO</strong> de Iberoamérica.</p>
            </div>
          </div>
        </section>

        {/* Objetivos Específicos */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Objetivos del programa postdoctoral</h2>
            <div className="h-1 w-24 bg-[#e87729] rounded mx-auto"></div>
          </div>
          <div className="space-y-4">
            {objetivosPostdoctorado.map((objetivo, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-[#0b742f]"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-[#0b742f]/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#0b742f] font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{objetivo}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Programa - Etapas */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#e87729] p-3 rounded-lg">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Programa Académico</h2>
            </div>

            {/* Diagrama de Etapas del Programa - Oculto según solicitud */}
            <div className="hidden mb-8">
              <div className="grid md:grid-cols-2 gap-8">

                {/* 1 Etapa */}
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-blue-600 mb-2">1 Etapa</h3>
                    <p className="text-gray-700 font-semibold">duración 6 meses</p>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-yellow-400 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Taller: Presentación de proyecto postdoctoral I
                    </div>

                    <div className="bg-orange-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Primer seminario de especialización
                    </div>

                    <div className="bg-green-400 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Segundo seminario de especialización
                    </div>

                    <div className="bg-green-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Taller: Presentación de proyecto postdoctoral II
                    </div>

                    <div className="bg-orange-600 text-white px-4 py-4 rounded-lg text-center text-sm leading-tight">
                      <p className="font-semibold mb-2">Elaboración de protocolo; revisión y aprobación de tópicos, inscripción a Líneas de Generación y Aplicación del Conocimiento (LGAC), donde recibirá su carta de aceptación.</p>
                      <p className="text-xs italic bg-orange-700 rounded px-2 py-1 mt-2">
                        • Al final de la primera etapa se otorgará constancia de diplomado "Competencias para la Investigación multidisciplinaria"
                      </p>
                    </div>
                  </div>
                </div>

                {/* 2 Etapa */}
                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-blue-600 mb-2">2 Etapa</h3>
                    <p className="text-gray-700 font-semibold">duración 6 meses</p>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-green-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Elaboración de plan de trabajo a desarrollar para la etapa de investigación.
                    </div>

                    <div className="bg-green-400 text-white px-4 py-4 rounded-lg text-center text-sm leading-tight">
                      Estancia de investigación en modalidad virtual a través de la Red Intercontinental de Investigaciones sobre Educación en Línea o en modalidad presencial mediante carta aceptación en alguna institución educativa.
                    </div>

                    <div className="bg-teal-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Memoria de plan de trabajo.
                    </div>

                    <div className="bg-cyan-500 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Invitación como conferencista magistral en modalidad virtual.
                    </div>

                    <div className="bg-blue-400 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      Defensa en modalidad virtual ante el comité de expertos de su proyecto postdoctoral.
                    </div>

                    <div className="bg-blue-600 text-white px-4 py-3 rounded-lg text-center font-semibold text-sm">
                      • Publicación académica arbitrada en formato electrónico.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Primera Etapa */}
            <div className="mb-8">
              <h4 className="text-xl font-bold text-[#0b742f] mb-4">Primera etapa (6 meses)</h4>
              <p className="text-gray-700 mb-4">
                Generación de competencias para el desarrollo y la divulgación de la ciencia que fortalezca la cultura científica en la sociedad. Duración de cada seminario: 8 semanas.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {primeraEtapa.map((seminario, index) => (
                  <div key={index} className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                    <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{seminario}</span>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-700">
                  <strong>Al final de la primera etapa se otorgará constancia de diplomado "Competencias para la Investigación multidisciplinaria"</strong>
                </p>
              </div>
            </div>

            {/* Segunda Etapa */}
            <div>
              <h4 className="text-xl font-bold text-[#0b742f] mb-4">Segunda etapa (6 meses)</h4>
              <p className="text-gray-700 mb-4">Duración de cada taller: 30 días.</p>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                {segundaEtapa.map((taller, index) => (
                  <div key={index} className="flex items-start gap-3 bg-orange-50 rounded-lg p-4">
                    <CheckCircle className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{taller}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-purple-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Estancia de investigación en modalidad virtual a través de la Red Intercontinental de Investigaciones sobre Educación en Línea o en modalidad presencial.</span>
                </div>
                <div className="flex items-start gap-3 bg-purple-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">*Publicación artículo científico arbitrado en formato electrónico en la revista RIDE (Revista Iberoamericana para la Investigación y el Desarrollo Educativo).</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Líneas de Investigación */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Lineas de generacion y aplicacion del conocimiento (LGAC)</h2>
            <div className="h-1 w-24 bg-[#e87729] rounded mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Conoce las líneas de investigación disponibles en nuestros programas postdoctorales
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {lineasInvestigacion.map((linea, index) => {
              const IconComponent = linea.icono;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100"
                >
                  <div className={`${linea.color} p-4 rounded-lg mb-6`}>
                    <div className="flex items-center gap-3 text-white">
                      <IconComponent className="w-8 h-8" />
                      <h3 className="text-lg font-bold">{linea.titulo}</h3>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {linea.lineas.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#0b742f] mt-1 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Enlace a información completa */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 text-center border border-blue-200">
            <div className="mb-6">
              <BookOpen className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">¿Quieres conocer más detalles?</h3>
              <p className="text-gray-600">
                Consulta la información completa de las Líneas de Generación y Aplicación del Conocimiento (LGAC),
                incluyendo coordinadores, proyectos de investigación y requisitos específicos.
              </p>
            </div>
            <Link
              to="/lgac-postdoctorados"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors duration-300"
            >
              <ArrowRight className="w-5 h-5" />
              Ver LGAC Completas
            </Link>
          </div>
        </section>

        {/* Proceso de Inscripción */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#0b742f] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Proceso de Inscripción</h2>
            </div>
            <p className="text-gray-700 mb-6">
              La filosofía de los eventos organizados por el CENID es ofrecer a todos los empresarios, investigadores, docentes y público en general la oportunidad que comparta su conocimiento a todo el mundo y sin restricciones. Sin embargo, existen costos de organización y de desarrollo del evento que es necesario cubrir, por lo que le invitamos a colaborar en la sustentabilidad del proyecto mediante el pago voluntario de la Constancia de Participación y acceso a Actas Electrónicas.
            </p>
            <h4 className="text-lg font-bold text-gray-900 mb-4">Documentos requeridos:</h4>
            <div className="space-y-3">
              {documentosRequeridos.map((documento, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{documento}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-700 mt-6">
              En cuanto se reciba en el CENID la documentación completa, se enviará un acuse de recibo por correo electrónico a cada participante.
            </p>
          </div>
        </section>

        {/* Costos del Programa */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <DollarSign className="w-10 h-10 text-white" />
              <h2 className="text-3xl font-bold">Costos del Programa</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <div className="mb-6">
                  <p className="text-orange-100 text-lg mb-2">Costo total</p>
                  <p className="text-4xl font-bold">{costos.total}</p>
                  <p className="text-orange-100">Puede ser cubierto durante los 12 meses ({costos.mensualidad} mensual)</p>
                </div>

                <h4 className="text-xl font-bold mb-4">El costo incluye:</h4>
                <div className="space-y-2">
                  {costosIncluidos.map((costo, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                      <span className="text-white/95">{costo}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h4 className="text-xl font-bold mb-4">Información Bancaria</h4>
                {datosBancarios ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-orange-100">Banco:</p>
                      <p className="font-semibold">{datosBancarios.banco}</p>
                    </div>
                    <div>
                      <p className="text-orange-100">Titular:</p>
                      <p className="font-semibold">{datosBancarios.titular}</p>
                    </div>
                    <div>
                      <p className="text-orange-100">Número de Cuenta:</p>
                      <p className="font-semibold">{datosBancarios.numeroCuenta}</p>
                    </div>
                    <div>
                      <p className="text-orange-100">Sucursal:</p>
                      <p className="font-semibold">{datosBancarios.sucursal}</p>
                    </div>
                    <div>
                      <p className="text-orange-100">CLABE Interbancaria:</p>
                      <p className="font-semibold">{datosBancarios.clabe}</p>
                    </div>
                    <div>
                      <p className="text-orange-100">SWIFT:</p>
                      <p className="font-semibold">{datosBancarios.swift}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-white/70">Cargando información bancaria...</p>
                )}
                <div className="mt-4 p-3 bg-white/5 rounded">
                  <p className="text-white/95">
                    <strong>Todos los pagos deberán ser reportados al correo:</strong><br />
                    {modalidad.contacto}
                  </p>
                </div>
              </div>
            </div>

            {/* Información sobre artículo científico */}
            <div className="bg-white/10 rounded-lg p-6 mb-6">
              <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Artículo Científico Incluido
              </h4>
              <div className="space-y-4">
                <p className="text-white/95">
                  Como producto final cada participante deberá elaborar un artículo científico, el artículo será publicado en la revista
                  <strong> RIDE Revista Iberoamericana para la Investigación y el Desarrollo Educativo ISSN 2007</strong>
                  (Se encuentra dentro del padrón de excelencia del SECIHTI).
                </p>

                <div className="bg-red-500/20 border border-red-300/30 rounded p-4">
                  <h5 className="font-bold text-white mb-2">Notas Importantes:</h5>
                  <ul className="space-y-2 text-white/90">
                    <li>• Se tiene contemplado que a lo largo del programa postdoctoral y por el mismo costo el participante elabore un artículo científico.</li>
                    <li>• La publicación deberá sujetarse a los lineamientos de la editorial CENID y ser entregada a la coordinación del postdoctorado al final del programa.</li>
                    <li>• De no respetarse la fecha de entrega (al final del programa) la coordinación del postdoctorado queda libre de cualquier responsabilidad sobre la publicación.</li>
                    <li>• Para que el artículo sea publicado deberá cumplir estrictamente con las directrices trabajo final y pasar positivamente dos procesos: dictaminación del comité editorial y la evaluación por pares.</li>
                    <li>• En la publicación del artículo solo aparecerá el autor que cubre la cuota, si desea agregar un autor más al artículo, deberá de cubrir un pago extra.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Formas de pago */}
            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Formas de Pago
              </h4>
              <div className="space-y-4">
                <div className="bg-yellow-500/20 border border-yellow-300/30 rounded p-4">
                  <p className="font-bold text-white mb-2">Por favor realiza tus depósitos sólo en la cuenta autorizada</p>
                  <ul className="space-y-1 text-white/90">
                    <li>• Los pagos deberán efectuarse vía banca electrónica</li>
                    <li>• Los pagos realizados fuera de México se podrán realizar vía: PayPal o Western Union</li>
                    <li>• Se deberá agregar a cada mensualidad el 5% por la comisión de transferencia</li>
                    <li>• Solicitar datos para transferencias al correo: {modalidad.contacto}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comité del Postdoctorado */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#0b742f] p-3 rounded-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Comité del Postdoctorado</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {comitePostdoctorado.map((miembro, index) => (
                <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                  <Award className="w-5 h-5 text-[#0b742f] flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{miembro}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Plantilla Docente */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl p-8 md:p-12 border border-amber-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-amber-600 p-3 rounded-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Plantilla Docente</h2>
            </div>

            <div className="space-y-8">
              {programaData?.plantillaDocente?.length > 0 ? (
                programaData.plantillaDocente.map((docente, index) => (
                  <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="grid md:grid-cols-4 gap-6 items-start">
                      <div className="flex justify-center">
                        <img
                          src={docente.image}
                          alt={docente.name}
                          className="w-32 h-32 rounded-full object-cover shadow-md"
                          loading="lazy"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                          {docente.name} {docente.subtitle ? docente.subtitle : ''}
                        </h3>
                        <p className="text-gray-700 leading-relaxed">{docente.bio}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">No hay información de la plantilla docente disponible en este momento.</p>
              )}
            </div>
          </div>
        </section>

        {/* Reglamento */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-[#0b742f] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Reglamento</h2>
            </div>

            <div className="space-y-8">
              {/* Capítulo 1 */}
              <div className="border-l-4 border-[#0b742f] pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Capítulo 1: Los objetivos</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 1.</p>
                    <p className="text-gray-700 mb-3">
                      El programa tiene por objetivo apoyar el proceso de construcción de un núcleo de estudios sobre desarrollo en América Latina mediante:
                    </p>
                    <ul className="space-y-2 text-gray-700 ml-4">
                      <li>• Formación de recursos humanos que dominen los últimos conocimientos en la materia y sean capaces de formular propuestas críticas, novedosas y alternativas</li>
                      <li>• Producción de trabajos de alto nivel</li>
                      <li>• Formulación de proyectos de investigación o desarrollo que recojan los intereses de grupos y comunidades de base</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Capítulo 2 */}
              <div className="border-l-4 border-[#e87729] pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Capítulo 2: La gestión del programa de Postdoctorado</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 2.</p>
                    <p className="text-gray-700 mb-3">
                      La gestión del programa será ejercida por un Comité del Postdoctorado
                    </p>
                    <p className="text-gray-700">
                      El comité será conformado por el coordinador académico y el responsable del programa de Postdoctorado.
                    </p>
                  </div>
                </div>
              </div>

              {/* Capítulo 3 */}
              <div className="border-l-4 border-[#0b742f] pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Capítulo 3: Las atribuciones del Comité de Postdoctorado</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 3.</p>
                    <p className="text-gray-700 mb-3">
                      El Comité Postdoctorado tiene la atribución de evaluar la propuesta del candidato, realizar el monitoreo del trabajo de investigación y emitir el dictamen final al completarse el periodo. Ello incluye:
                    </p>
                    <ul className="space-y-2 text-gray-700 ml-4">
                      <li>• Evaluación de la propuesta de investigación en base a la pertinencia del tema en función de los objetivos propuestos por el Comité del Postdoctorado, la amplitud y actualidad de la revisión de la literatura sobre el tema, y la viabilidad del proyecto en su conjunto, incluyendo antecedentes del candidato, edad y demás información del currículo vitae</li>
                      <li>• Asimismo se considerará si el tutor ha supervisado y entregado los informes de sus previos postdoctorantes en regla y tiempo</li>
                      <li>• El seguimiento de los resultados parciales y potenciales publicaciones</li>
                      <li>• La evaluación del trabajo final que deberá incluir al menos una publicación en revista científica arbitrada, indexada y de circulación internacional</li>
                      <li>• La emisión del dictamen final que será turnado al Colegiado del Comité del Postdoctorado</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Capítulo 4 */}
              <div className="border-l-4 border-[#e87729] pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Capítulo 4: La admisión, derechos y obligaciones del Postdoctorante</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 4.</p>
                    <p className="text-gray-700 mb-3">
                      Para ser admitido en el programa de Postdoctorado, el candidato deberá cumplir con los siguientes requisitos:
                    </p>
                    <ul className="space-y-2 text-gray-700 ml-4">
                      <li>• Poseer título de Doctor por una universidad reconocida internacionalmente</li>
                      <li>• Tener perfil de investigador, demostrando dominio actualizado en la línea de investigación para la cual presenta el proyecto de investigación mediante artículos ya publicados en el tema</li>
                      <li>• Presentar un proyecto de investigación que contenga los objetivos, antecedentes, metodología y guión de desarrollo de la temática, demostrando actualización de la literatura sobre el tema</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 5.</p>
                    <p className="text-gray-700">
                      El Postdoctorante deberá tener asistencia regular de un tutor, que será un profesor de la planta permanente del Centro de Estudios e investigaciones para el desarrollo Docente CENID A.C.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 6.</p>
                    <p className="text-gray-700">
                      El tutor es responsable de que su postdoctorante entregue en tiempo y regla los informes semestrales. El tutor deberá avalar la calidad necesaria de todos los informes y el informe final.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 7.</p>
                    <p className="text-gray-700 mb-3">
                      El periodo de ejecución de la investigación será determinado de común acuerdo entre el candidato y el Comité de Postdoctorado. Durante el periodo de investigación el Postdoctorante deberá:
                    </p>
                    <ul className="space-y-2 text-gray-700 ml-4">
                      <li>• Entregar avances parciales</li>
                      <li>• Dictar seminarios, cursos, conferencias a la comunidad académica</li>
                      <li>• Colocar en nota a pie de página en todos los trabajos la siguiente nota: "Este trabajo fue realizado como parte de las actividades de postdoctorado en (nombre del programa postdoctoral) del Centro de Estudios e investigaciones para el desarrollo Docente CENID A.C.</li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 8.</p>
                    <p className="text-gray-700">
                      Es responsabilidad del propio candidato obtener los recursos necesarios para el desarrollo de la investigación. El Centro de Estudios e investigaciones para el desarrollo Docente CENID A.C. no cuenta con fondos para becas.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-800 mb-2">Art. 9.</p>
                    <p className="text-gray-700">
                      Una vez finalizada la estancia postdoctoral, y si el candidato cumplió con los requisitos, obtendrá un Certificado de Postdoctorado por parte del Centro de Estudios e investigaciones para el desarrollo Docente CENID A.C señalando que cumplió satisfactoriamente.
                    </p>
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-yellow-800 font-semibold">
                        Téngase en cuenta que el certificado de Postdoctorado no es un grado académico
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Capítulo 5 */}
              <div className="border-l-4 border-[#0b742f] pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Capítulo 5: Otros</h3>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">Art. 10.</p>
                  <p className="text-gray-700">
                    Casos y circunstancias omisas serán solucionados por el Comité de Postdoctorado.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Llamada a la acción */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">¿Listo para comenzar tu Postdoctorado?</h2>
            <p className="text-xl text-green-100 mb-8">
              Únete a nuestro programa postdoctoral y forma parte de la comunidad académica de excelencia del CENID
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`mailto:${modalidad.contacto}`}
                className="inline-flex items-center gap-2 bg-white text-[#0b742f] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-300"
              >
                <Mail className="w-5 h-5" />
                Solicitar Información
              </a>
              <a
                href="https://www.cenid.org/documentos/certificado_final.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#e87729] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#d66820] transition-colors duration-300"
              >
                <Download className="w-5 h-5" />
                Ver Ejemplo de Certificado
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Postdoctorados;